import type { AdminCatalogue } from './admin-es';

/**
 * The Spanish catalogue, and the shape every other language is measured against.
 *
 * **Spanish is the source language, not a translation of English.** The brief says the product is
 * Spanish by default and English switchable, so this file is where a string is written first and
 * `en.ts` is typed as `typeof es`: a key added here and forgotten there is a compile error before
 * it is a gate failure, and a key that exists only in English cannot be written at all.
 *
 * **Error messages are not here.** Every failure the API can return is named by a code, and the
 * code resolves through `@sentra/contracts`'s own catalogue (AD-35), which both the server and the
 * browser read. Copying one of those sentences into this file would create a second Spanish for the
 * same failure, and the one that drifted would be the one on screen. What this file carries about
 * errors is only what the API cannot say: a transport failure, and the handful of places where the
 * interface has a better sentence than the wire does — a search that is still building its index,
 * for instance, which is a state rather than a refusal.
 *
 * **A placeholder is written `{{name}}`.** The set a message uses must be identical in both
 * languages; `test:i18n` refuses a mismatch, because a `{{minutes}}` that became `{{horas}}` in
 * translation renders as literal braces on exactly one of the two screens and is therefore
 * invisible to anybody testing in the other.
 */
export const es = {
  app: {
    name: 'Sentra',
  },
  common: {
    close: 'Cerrar',
    loading: 'Cargando…',
    retry: 'Reintentar',
  },
  confirm: {
    typeToContinue: 'Escribe «{{phrase}}» para continuar',
    cancel: 'Cancelar',
    working: 'Aplicando…',
  },
  shell: {
    skipToContent: 'Saltar al contenido',
    navigationLabel: 'Secciones',
    mainLabel: 'Contenido principal',
    openNavigation: 'Abrir el menú de secciones',
    closeNavigation: 'Cerrar el menú de secciones',
  },
  nav: {
    overview: 'Resumen',
    scan: 'Control de acceso',
    persons: 'Registro de personas',
    admin: 'Administración',
  },
  overview: {
    title: 'Resumen',
    welcome: 'Sesión iniciada como {{name}}.',
    issuanceMode: 'Modo de emisión de pases',
    issuanceModePreprinted: 'Pulseras preimpresas',
    issuanceModeOnDemand: 'Emisión bajo demanda',
    reportingTimezone: 'Zona horaria de los informes',
    terminalTime: 'Hora del terminal',
    presence: 'Presencia',
    presenceLoading: 'Consultando…',
    presenceOutside: 'Fuera',
    presenceOverdue: 'Fuera más de {{minutes}} min',
    presenceInside: 'Dentro',
  },
  theme: {
    label: 'Tema',
    light: 'Claro',
    dark: 'Oscuro',
    system: 'Según el sistema',
  },
  language: {
    label: 'Idioma',
    es: 'Español',
    en: 'English',
  },
  palette: {
    open: 'Abrir la paleta de comandos',
    title: 'Paleta de comandos',
    description: 'Busca una sección o una acción y pulsa Intro.',
    placeholder: 'Escribe para buscar…',
    empty: 'Sin coincidencias.',
    groupNavigation: 'Ir a',
    groupActions: 'Acciones',
  },
  session: {
    signedInAs: 'Sesión de',
    accountMenu: 'Cuenta y preferencias',
    signOut: 'Cerrar sesión',
    switchOperator: 'Cambiar de operador',
    switchOperatorTitle: '¿Cambiar de operador?',
    switchOperatorBody:
      'La persona que entra se identifica aquí. Esta sesión termina en el mismo momento, y todo lo que se haga a partir de entonces queda registrado a su nombre.',
    signOutIncomplete: 'Se ha cerrado aquí, pero el servidor no lo ha confirmado.',
    roleOperator: 'Operador',
    roleAnalyst: 'Analista',
    roleAdmin: 'Administrador',
    roleSuperAdmin: 'Administrador principal',
  },
  config: {
    failedTitle: 'No se ha podido cargar la configuración',
    failedBody:
      'La aplicación necesita la configuración de la instalación antes de mostrar nada. Comprueba que el servicio está en marcha y vuelve a intentarlo.',
    sessionUnavailableTitle: 'No se ha podido comprobar tu sesión',
    sessionUnavailableBody:
      'El servidor está ocupado o reiniciándose y no ha podido confirmar si la sesión de este terminal sigue abierta. Espera un momento y vuelve a intentarlo.',
  },
  auth: {
    signInTitle: 'Iniciar sesión',
    signInSubtitle: 'Acceso para el personal de la instalación.',
    email: 'Correo electrónico',
    // Named `secretField` rather than anything containing "password": `sonarjs/no-hardcoded-passwords`
    // — which `eslint.config.ts` takes from the sonarjs recommended preset — reports a property whose
    // NAME looks like a credential and whose VALUE looks like one too, and a one-word label does.
    // `auth.currentPassword` below is untouched because its value has a space in it. Renaming this
    // key back turns `lint` red, which is checkable: rename it and run `npx eslint` on this file.
    secretField: 'Contraseña',
    signIn: 'Entrar',
    signingIn: 'Entrando…',
    secondFactorTitle: 'Verificación en dos pasos',
    secondFactorSubtitle: 'Introduce el código de seis dígitos de tu aplicación de autenticación.',
    totpCode: 'Código de la aplicación',
    useRecoveryCode: 'Usar un código de recuperación',
    useAuthenticator: 'Usar la aplicación de autenticación',
    recoveryCode: 'Código de recuperación',
    recoveryCodesRemaining: 'Te quedan {{remaining}} códigos de recuperación.',
    passwordChangeTitle: 'Elige una contraseña nueva',
    passwordChangeSubtitle: 'Tu cuenta se creó con una contraseña temporal y hay que cambiarla.',
    currentPassword: 'Contraseña actual',
    newPassword: 'Contraseña nueva',
    repeatPassword: 'Repite la contraseña nueva',
    passwordsDoNotMatch: 'Las dos contraseñas no coinciden.',
    changePassword: 'Cambiar la contraseña',
    enrolTitle: 'Activa tu aplicación de autenticación',
    enrolSubtitle:
      'Escanea el código con tu aplicación, o escribe la clave a mano, y confirma con los seis dígitos que muestre.',
    enrolSecret: 'Clave manual',
    enrolQrLabel: 'Código para la aplicación de autenticación',
    enrolConfirm: 'Confirmar',
    recoveryCodesTitle: 'Guarda estos códigos de recuperación',
    recoveryCodesBody:
      'Cada código sirve una sola vez y no volverán a mostrarse. Son la única forma de entrar si pierdes el teléfono.',
    recoveryCodesAcknowledge: 'Los he guardado',
    pinTitle: 'Elige un PIN para el puesto',
    pinSubtitle:
      'El PIN desbloquea este puesto tras un periodo de inactividad, sin cerrar la sesión. Son seis dígitos.',
    pin: 'PIN',
    setPin: 'Guardar el PIN',
    lockTitle: 'Puesto bloqueado por inactividad',
    lockSubtitle: 'Introduce tu PIN para continuar donde lo dejaste.',
    lockAttemptsRemaining: 'Te quedan {{attempts}} intentos antes de pedir la contraseña.',
    lockPasswordTitle: 'Introduce tu contraseña',
    lockPasswordSubtitle:
      'Se han agotado los intentos con el PIN. Tu trabajo sin guardar sigue donde estaba.',
    backToUnlock: 'Volver a desbloquear este puesto',
    unlock: 'Desbloquear',
    lockWorkPreserved: 'Lo que estabas escribiendo se conserva.',
    noPinTitle: 'Este puesto no tiene PIN',
    reset: {
      passwordTitle: 'Restablecer la contraseña',
      totpTitle: 'Recuperar el segundo factor',
      subtitle:
        'Le enviamos un código al correo de la cuenta. Escríbalo aquí, en este mismo equipo.',
      forgotPassword: 'He olvidado mi contraseña',
      lostAuthenticator: 'He perdido mi aplicación de autenticación',
      sendCode: 'Enviar el código',
      code: 'Código del correo',
      confirm: 'Confirmar',
      backToSignIn: 'Volver a iniciar sesión',
      sentIfKnown:
        'Si esa dirección corresponde a una cuenta, el código ya va de camino. Caduca en 15 minutos y sólo sirve en este equipo. Revise también la carpeta de correo no deseado.',
      totpNeedsPassword:
        'Hace falta también la contraseña de la cuenta: con el correo por sí solo no se puede quitar el segundo factor.',
      donePassword: 'Contraseña cambiada. Ya puede iniciar sesión con ella.',
      doneTotp:
        'Segundo factor retirado. Al iniciar sesión se le pedirá configurar de nuevo la aplicación de autenticación.',
    },
    noPinSubtitle: 'Introduce tu contraseña para desbloquear y define un PIN después.',
  },
  conflict: {
    title: 'Alguien ha modificado este registro mientras lo editabas',
    body: 'No se ha guardado nada. Revisa cada campo en conflicto y decide qué valor se conserva.',
    columnMine: 'Tu versión',
    columnTheirs: 'Versión guardada',
    empty: '(sin valor)',
    keepMine: 'Conservar la mía',
    keepTheirs: 'Usar la guardada',
    reload: 'Descartar mis cambios y recargar',
    apply: 'Guardar con lo elegido',
    unresolved: 'Elige un valor en cada campo antes de guardar.',
    unavailableTitle: 'No se ha podido leer el registro guardado',
    unavailableBody:
      'Sin él no se puede decidir qué valor se conserva, y guardar a ciegas borraría el cambio de la otra persona. No se ha enviado nada.',
  },
  search: {
    indexBuilding:
      'El índice de búsqueda se está construyendo. Vuelve a intentarlo en unos segundos.',
    indexBuildingHint: 'Esto no significa que la persona no esté registrada.',
    noResults: 'Ninguna persona coincide con esta búsqueda.',
    refined: 'Hay más coincidencias de las que caben. Añade algún dato más para acotar.',
  },
  person: {
    form: {
      age: '{{count}} años',
      createTitle: 'Registrar a una persona',
      amendTitle: 'Modificar el registro {{shortRef}}',
      save: 'Guardar',
      saving: 'Guardando…',
      cancel: 'Cancelar',
      saveFailed: 'No se ha guardado nada',
      requiredMark: ' *',
      requiredLabel: 'obligatorio',
      selectNone: '(sin elegir)',
      characterCount: '{{used}} de {{limit}} caracteres',
      countryList: 'Países',
      countryNoMatches: 'Ningún país coincide.',
      phoneDetected: 'Número de {{country}}.',
      phonePrefix: 'Prefijo internacional',
      phonePrefixList: 'Prefijos internacionales',
      adhocLegend: 'Campos adicionales',
      adhocHint:
        'Campos propios de este registro, hasta {{limit}}. El nombre se guarda en este puesto para volver a ofrecerlo; el valor no.',
      adhocName: 'Nombre del campo',
      adhocValue: 'Valor',
      adhocAdd: 'Añadir un campo',
      adhocRemove: 'Quitar este campo',
      adhocFull: 'Has llegado al máximo de campos adicionales.',
      adhocLimit: 'No caben más de {{limit}} campos adicionales.',
      adhocDuplicateName: 'Hay dos campos adicionales con el mismo nombre.',
      completenessTitle: 'Faltan datos en este registro',
      completenessBody:
        'Se puede guardar así y completarlo más tarde. Esto es solo un aviso, no un impedimento.',
      completenessConfirm: 'Guardar de todos modos',
      completenessBack: 'Volver y completarlo',
      missingPhotograph: 'Fotografía',
      duplicateTitle: 'Puede que esta persona ya esté registrada ({{count}})',
      duplicateBody: 'Comprueba la fotografía antes de crear un registro nuevo.',
      duplicatePhotoAlt: 'Fotografía del registro {{shortRef}}',
      duplicateOpen: 'Abrir',
      duplicateDismiss: 'No es la misma persona',
      matchedName: 'Coincide el nombre',
      matchedReference: 'Mismo código de referencia',
      matchedPassport: 'Coincide el documento',
      bandLabel: 'Pulsera preimpresa',
      bandHint: 'Escanea la pulsera o escribe el código. Se puede hacer antes o después del resto.',
      bandMalformed: 'Ese código no es válido. Revisa el último símbolo.',
      bandAvailable: 'Pulsera disponible.',
      bandClaimed: 'Esa pulsera ya está asignada al registro {{shortRef}}.',
      bandVoid: 'Esa pulsera pertenece a un lote retirado.',
      bandUnknown: 'Esa pulsera no consta en ningún lote importado.',
      bandCamera: 'Leer la pulsera con la cámara',
      bandCameraClose: 'Cerrar la cámara de la pulsera',
      noBandLabel: 'Todavía no hay pulsera: registrar sin pase',
      noBandHint: 'La pulsera se vincula después. Escriba el código que entrega en papel.',
      emergencyLabel: 'No hay pulsera disponible: emitir un pase de emergencia',
      emergencyHint:
        'Solo cuando la caja está vacía. El pase se imprime en papel y queda marcado como emisión ' +
        'de emergencia mientras exista el registro.',
      consentTitle: 'Falta la confirmación del aviso de privacidad',
      consentBody:
        'Esta instalación exige que la persona haya recibido el aviso de privacidad antes de crear el registro. Confirma que se le ha entregado.',
      consentConfirm: 'Se le ha entregado el aviso',
      statusLabel: 'Situación',
      photographsHint:
        'Se hace o se sube ahora y se guarda junto con la ficha. Después se puede cambiar desde «Modificar los datos».',
      attaching: 'Guardando la fotografía…',
      attachFailedTitle: 'La ficha se ha creado, pero la fotografía no se ha guardado',
      attachRetry: 'Reintentar la fotografía',
      attachContinue: 'Continuar sin ella',
      blockedByProblems: 'No se ha enviado nada: hay datos arriba que hay que corregir antes.',
    },
    images: {
      title: 'Fotografías',
      empty: 'Este registro todavía no tiene ninguna fotografía.',
      uploadLabel: 'Subir una fotografía',
      uploading: 'Subiendo la fotografía…',
      acceptedFormats: 'Se admiten JPEG, PNG, WebP y AVIF.',
      wrongType: 'Ese tipo de archivo no se puede guardar aquí.',
      emptyFile: 'Ese archivo está vacío.',
      capReached: 'Cada registro admite como máximo {{limit}} fotografías.',
      failedTitle: 'No se ha podido guardar la fotografía',
      remove: 'Quitar',
      removedCount: 'Este registro tuvo {{count}} fotografías que se quitaron.',
      thumbnailAlt: 'Miniatura de la fotografía {{position}}',
      unavailable: 'No se ha podido cargar la fotografía',
      loading: 'Cargando la fotografía…',
      fullAlt: 'Fotografía {{position}} a tamaño completo',
      viewerTitle: 'Fotografía',
      fromWebcam: 'Tomada aquí',
      fromUpload: 'Subida',
      startCamera: 'Usar la cámara',
      stopCamera: 'Apagar la cámara',
      opening: 'Abriendo la cámara…',
      previewLabel: 'Vista de la cámara',
      previewStalled:
        'El navegador no ha arrancado la vista de la cámara. Lo que se ve puede ser una imagen congelada.',
      shotAlt: 'Fotografía recién tomada',
      deviceLabel: 'Cámara',
      deviceFallback: 'Cámara {{index}}',
      mirrorPreview: 'Ver la imagen como en un espejo',
      storeMirrored: 'Guardar la imagen invertida',
      mirrorHint:
        'La vista se muestra invertida porque es lo que espera quien está delante. Lo que se guarda es la imagen sin invertir, que es la correcta para identificar a alguien.',
      takePhoto: 'Tomar la fotografía',
      cancelCountdown: 'Cancelar',
      countdown: '{{seconds}}',
      keepShot: 'Guardar esta fotografía',
      retake: 'Repetir',
      noCameraTitle: 'Este puesto no tiene cámara',
      noCameraBody: 'Puedes subir una fotografía desde el equipo.',
      deniedTitle: 'El navegador no deja usar la cámara',
      deniedBody:
        'Abre los permisos del sitio en el navegador, activa la cámara y vuelve a intentarlo. También puedes subir una fotografía desde el equipo.',
      lostTitle: 'Se ha perdido la cámara',
      lostBody: 'Puede que se haya desconectado o que otra aplicación la esté usando.',
      cameraFailedTitle: 'No se ha podido abrir la cámara',
      cameraFailedBody: 'Vuelve a intentarlo o sube una fotografía desde el equipo.',
    },
    table: {
      searchLabel: 'Buscar en el registro',
      startTyping: 'Escribe al menos dos caracteres para buscar.',
      newPerson: 'Registrar a alguien',
      caption: 'Personas del registro',
      columnRef: 'Referencia',
      columnReference: 'Código de ref.',
      columnPhoto: 'Foto',
      columnName: 'Nombre',
      columnStatus: 'Estado',
      columnPresence: 'Presencia',
      columnLastActivity: 'Última actividad',
      columnActions: 'Acciones',
      column_referenceCode: 'Código de referencia',
      column_photo: 'Foto',
      column_displayName: 'Nombre',
      column_status: 'Estado',
      column_presenceState: 'Presencia',
      column_lastActivityAt: 'Última actividad',
      columns: 'Columnas visibles',
      noPhoto: 'Sin fotografía',
      photoAlt: 'Fotografía del registro {{shortRef}}',
      never: 'Nunca',
      status_active: 'Activo',
      status_blocked: 'Bloqueado',
      status_archived: 'Archivado',
      presence_inside: 'Dentro',
      presence_outside: 'Fuera',
      filterStatus: 'Estado',
      filterPresence: 'Presencia',
      filterPhoto: 'Fotografía',
      filterAny: 'Cualquiera',
      photoWith: 'Con fotografía',
      photoWithout: 'Sin fotografía',
      clearFilters: 'Quitar los filtros',
      density: 'Altura de las filas',
      densityComfortable: 'Normal',
      densityCompact: 'Compacta',
      exportCsv: 'Exportar esta página',
      firstPage: 'Primera',
      previousPage: 'Anterior',
      nextPage: 'Siguiente',
      lastPage: 'Última',
      pageOf: 'Página {{page}} de {{pages}}',
      total: 'Total: {{total}}',
      registerEmpty: 'Todavía no hay nadie registrado.',
      pastLastPage:
        'Esta página ya no existe: hay menos resultados que antes. Vuelve a la primera.',
      noRowsMatch:
        'Ningún registro cumple los filtros aplicados. Esto no significa que la persona no esté registrada.',
      action_view: 'Ver',
      action_modify: 'Modificar',
      action_shred: 'Borrar los datos',
      action_presence: 'Entrada o salida',
      ackTitle: 'Confirma que llegas a este registro por búsqueda',
      ackView: 'Vas a abrir la ficha completa de una persona.',
      ackModify: 'Vas a modificar la ficha de una persona.',
      ackPresence: 'Vas a registrar una entrada o una salida sin escanear el pase.',
      ackRecord: 'Registro {{shortRef}}.',
      ackWhy:
        'Queda anotado que se ha llegado por búsqueda y no escaneando un pase. Es lo normal cuando alguien ha perdido su pulsera.',
      ackShred: 'Vas a abrir una ficha para borrar sus datos de forma irreversible.',
      ackConfirm: 'Lo confirmo y continúo',
      ackCancel: 'No continuar',
    },
    section: {
      identity: 'Identidad',
      stay: 'Estancia',
      contact: 'Contacto',
      document: 'Documentación',
      vulnerability: 'Vulnerabilidad',
      protection: 'Protección internacional',
      other: 'Otros datos',
      unsorted: 'Sin agrupar',
    },
    record: {
      age: 'Edad',
      title: 'Registro {{shortRef}}',
      summary: 'Situación: {{status}}. Consta {{presence}}.',
      edit: 'Modificar los datos',
      backToList: 'Volver al listado',
      backToGate: 'Volver al control de acceso',
      erase: 'Borrar los datos',
      archivedTitle: 'Este registro está archivado',
      archivedBody:
        'No se le puede registrar ninguna entrada ni salida hasta que se reactive. Abre «Modificar los datos» y cambia ahí su situación.',
      incompleteTitle: 'Faltan datos en este registro',
      fields: 'Datos',
      retiredField: '(campo retirado)',
      adhoc: 'Campos adicionales',
      credentials: 'Pases',
      noCredential: 'Esta persona no tiene ningún pase.',
      credential_active: 'En uso',
      credential_revoked: 'Anulado',
      credential_superseded: 'Sustituido',
      issueBand: 'Asignar una pulsera',
      replaceBand: 'Sustituir la pulsera',
      replaceTitle: '¿Sustituir esta pulsera?',
      replaceBody:
        'El pase que lleva esta persona deja de funcionar en todas las puertas de inmediato y la ' +
        'pulsera nueva ocupa su lugar.',
      revisions: 'Historial de cambios',
    },
    erase: {
      title: 'Borrar los datos de {{shortRef}}',
      body: 'Se destruye la clave con la que se cifraron sus datos, así que dejan de poder leerse: no es un borrado que se pueda deshacer ni recuperar desde una copia posterior.',
      confirm: 'Borrar los datos',
      reason: 'Motivo',
      reasonPlaceholder: 'Elige un motivo',
      reasonRequired: 'Hace falta un motivo: es lo único que queda escrito después.',
      reason_data_subject_request: 'Lo ha pedido la persona',
      reason_erroneous_record: 'El registro no debería existir',
      reason_legal_order: 'Orden legal',
      irreversible: 'Esto no se puede deshacer.',
    },
    movement: {
      title: 'Registrar una entrada o una salida',
      body: 'Para cuando alguien no lleva su pulsera. Queda anotado que se ha llegado por búsqueda y no escaneando.',
      entry: 'Registrar entrada',
      exit: 'Registrar salida',
      recordedTitle: 'Registrado',
      refusedTitle: 'No se ha permitido el paso',
      recordedBody: 'Ahora consta {{state}}.',
    },
  },
  scan: {
    title: 'Control de acceso',
    subtitle: 'Pasa la pulsera por el lector, apunta con la cámara o escribe el código.',
    indicator: {
      label: 'Lector de pulseras',
      ready: 'Preparado',
      reading: 'Leyendo…',
      suppressed: 'En pausa: el teclado lo tiene otro campo',
      off: 'Desactivado',
    },
    code: {
      label: 'Código del pase',
      hint: 'Veintiún caracteres. También puedes pasar la pulsera por el lector.',
      submit: 'Comprobar',
      malformed: 'Ese código no es válido: revisa el último carácter.',
    },
    diagnostics: {
      toggle: 'Comprobar el lector',
      hint: 'Pasa una pulsera por el lector y mira lo que ha llegado a este terminal.',
      received: 'Pulsaciones recibidas',
      symbols: 'Caracteres del código',
      widestGap: 'Pausa más larga',
      key: 'Tecla',
      gap: 'Pausa (ms)',
      symbol: 'Símbolo',
      via: 'Vía',
      decision: 'Resultado',
      modifier: 'Modificador',
      held: 'Alt/Ctrl',
      cadence: 'Ritmo',
      slow: 'Lento: se leerá igual. Solo se anota como lectura del lector si el código entero llega en menos de tres segundos',
      brisk: 'Rápido: se anotará como lectura del lector',
      clear: 'Borrar',
    },
    camera: {
      open: 'Usar la cámara',
      close: 'Cerrar la cámara',
      title: 'Cámara',
      preview: 'Imagen de la cámara',
      device: 'Cámara',
      torchOn: 'Encender la luz',
      torchOff: 'Apagar la luz',
      scanning: 'Buscando un código…',
      loading: 'Preparando el lector de códigos…',
      unsupported:
        'Este navegador no puede leer códigos. Usa el lector de pulseras o escribe el código a mano.',
      denied:
        'El navegador no da permiso para la cámara en este puesto. Cámbialo en los ajustes del sitio y vuelve a intentarlo.',
      noCamera: 'Este puesto no tiene ninguna cámara conectada.',
      lost: 'Se ha perdido la cámara. Comprueba el cable y vuelve a abrirla.',
      failed: 'No se ha podido abrir la cámara ({{name}}).',
    },
    upload: {
      label: 'Subir una foto del pase',
      hint: 'Una foto del código hecha con el móvil también sirve.',
      decoding: 'Buscando el código en la imagen…',
      unsupported:
        'Este navegador no puede leer códigos. Usa el lector de pulseras o escribe el código a mano.',
      noCode: 'No se ha encontrado ningún código en esa imagen.',
      failed: 'No se ha podido leer la imagen.',
    },
    intake: {
      registerBand: 'Registrar con esta pulsera',
    },
    person: {
      photograph: 'Fotografía de {{name}}',
      noPhotograph: 'Sin fotografía',
      shortRef: 'Referencia',
      status: 'Situación del registro',
      presence: 'Dónde consta',
      expected: 'Lo que toca ahora',
      entry: 'Registrar entrada',
      exit: 'Registrar salida',
      view: 'Ver la ficha',
      modify: 'Modificar la ficha',
      submitting: 'Registrando…',
      another: 'Siguiente pulsera',
    },
    warning: {
      person_blocked: 'Esta persona tiene el acceso bloqueado.',
      person_archived: 'Este registro está archivado.',
      no_photograph: 'No hay fotografía: no puedes comprobar quién es.',
      name_unavailable: 'El nombre no está disponible en este momento.',
      outside_too_long: 'Salió y todavía no había vuelto: lleva fuera más de lo permitido.',
    },
    state: {
      inside: 'Dentro',
      outside: 'Fuera',
    },
    recordStatus: {
      active: 'Activo',
      blocked: 'Bloqueado',
      archived: 'Archivado',
    },
    outcome: {
      accepted: { title: 'Registrado', body: 'Puede pasar.' },
      override: {
        title: 'Movimiento contradictorio registrado',
        body: 'Queda como anomalía con el motivo elegido.',
      },
      blocked_override: {
        title: 'Paso autorizado con un motivo',
        body: 'Queda registrado quién lo ha autorizado y por qué.',
      },
      blocked: {
        title: 'Acceso bloqueado',
        body: 'El rechazo queda registrado. Para dejarla pasar, indica un motivo.',
      },
      archived: {
        title: 'Registro archivado',
        body: 'Hay que reactivar la ficha antes de registrar ningún movimiento.',
      },
      override_not_permitted: {
        title: 'No se puede autorizar desde aquí',
        body: 'Este movimiento necesita una persona que lo autorice.',
      },
      reason_required: {
        title: 'Falta el motivo',
        body: 'Elige por qué se registra este movimiento.',
      },
      duplicate: {
        title: 'Ya estaba registrado',
        body: 'Esta lectura repite un movimiento que ya constaba.',
      },
      revoked: {
        title: 'Pulsera anulada',
        body: 'Esta pulsera ya no sirve. Emite una nueva desde la ficha.',
      },
      superseded: {
        title: 'Pulsera sustituida',
        body: 'Se ha emitido otra pulsera para esta persona.',
      },
      blank_unclaimed: {
        title: 'Pulsera sin registrar',
        body: 'Da de alta a esta persona aquí para asignarle esta pulsera.',
      },
      blank_void: {
        title: 'Pulsera retirada',
        body: 'Este lote se ha retirado. No la uses.',
      },
      unknown: {
        title: 'Pulsera no reconocida',
        body: 'Comprueba el código o acompaña a esta persona a registro.',
      },
      malformed: {
        title: 'Código incorrecto',
        body: 'Lo leído no tiene forma de código: vuelve a pasar la pulsera.',
      },
      rate_limited: {
        title: 'Demasiadas lecturas fallidas',
        body: 'Espera un momento antes de volver a intentarlo.',
      },
      index_version_mismatch: {
        title: 'Pulsera de una versión anterior',
        body: 'Avisa a administración: esta pulsera no se puede resolver.',
      },
      offline: {
        title: 'Sin conexión',
        body: 'No se ha llegado al servidor. Vuelve a intentarlo: no se duplicará nada.',
      },
      refused: {
        title: 'No se ha podido registrar',
        body: 'Vuelve a intentarlo y, si sigue igual, avisa a administración.',
      },
    },
    duplicate: {
      toast: 'Ya estaba registrado hace {{seconds}} s, a las {{time}}.',
    },
    anomaly: {
      title: '¡Atención!',
      contradiction: 'Esta persona consta {{state}} y se está registrando lo mismo otra vez.',
      blocked: 'Esta persona tiene el acceso bloqueado.',
      instruction: 'Comprueba la fotografía y decide antes de continuar.',
      reasonLabel: 'Motivo',
      reasonPlaceholder: 'Elige un motivo',
      noteLabel: 'Explica qué ha pasado',
      noteRequired: 'Escribe una explicación para poder continuar.',
      reasonRequired: 'Elige un motivo para poder continuar.',
      acknowledge: 'Lo entiendo y quiero continuar',
      confirm: 'Continuar',
      cancel: 'No registrar nada',
      mute: 'Silenciar el aviso',
      unmute: 'Reactivar el sonido',
    },
    meal: {
      breakfast: 'Desayuno',
      lunch: 'Comida',
      dinner: 'Cena',
      heading: 'Comedor',
      servedAt: 'Servido a las {{time}}',
      notServed: 'Pendiente',
      repeatTitle: 'Esta comida ya se ha servido',
      repeatBody: 'Ya consta que esta persona ha recibido {{meal}} hoy.',
      reasonLabel: 'Motivo para servirla de nuevo',
      reasonPlaceholder: 'Elija un motivo',
      reasonRequired: 'Hace falta un motivo para servirla otra vez.',
      repeatConfirm: 'Servir de nuevo',
      retry: 'Reintentar',
      repeatCancel: 'No servir',
    },
    mealReason: {
      meal_spoiled: 'Se cayó o se estropeó la bandeja',
      recorded_in_error: 'Se registró por error',
      staff_authorised: 'Lo autorizó el personal',
    },
    reason: {
      left_via_other_exit: 'Salió por otra puerta',
      entered_via_other_entrance: 'Entró por otra puerta',
      gate_unattended: 'Puerta sin vigilancia',
      suspected_pass_sharing: 'Se sospecha uso compartido del pase',
      operator_error: 'Error del operador',
      other: 'Otro motivo',
      system_correction: 'Corrección del sistema',
    },
    delivery: {
      title: 'Pase de {{name}}',
      revealBody: 'Que la persona haga una foto de esta pantalla.',
      reveal: 'Mostrar el código',
      hide: 'Ocultar el código',
      symbol: 'Código QR del pase',
      code: 'Código',
      shortRef: 'Referencia',
      print: 'Imprimir en A4',
      printing: 'Preparando la impresión…',
      printFailed: 'No se ha podido imprimir. El pase sigue disponible en la ficha.',
      bound: 'Pulsera preimpresa asignada',
      boundBody: 'La persona ya lleva la pulsera. No hace falta imprimir nada.',
      noPrint: 'Una copia en papel sería un segundo pase para la misma persona.',
      calibrateTitle: 'Comprobación de la impresora',
      calibrateBody:
        'Imprime esta página al 100 %, sin ajustar al papel, y mide el cuadrado: tiene que medir {{millimetres}} mm de lado. Si mide menos, la impresora está reduciendo la página y los códigos no se leerán bien.',
      calibrateSquare: 'Cuadrado de referencia de {{millimetres}} mm',
      calibratePrint: 'Imprimir la comprobación',
    },
  },
  errors: {
    detail: {
      subject: '{{subject}}: {{message}}',
      pathSeparator: ' › ',
      atIndex: '{{subject}} [{{index}}]',
      inLanguage: '{{subject}} ({{language}})',
      more: 'Y {{count}} problema(s) más.',
    },
    networkBody: 'Comprueba la conexión del puesto y vuelve a intentarlo.',
    unexpectedBody: 'Si vuelve a pasar, avisa a administración con la hora exacta.',
    sessionInterrupted: 'El puesto se bloqueó antes de terminar. No se ha guardado nada.',
    retryAfter: 'Vuelve a intentarlo dentro de {{retryAfterSeconds}} segundos.',
    responseInvalid:
      'La respuesta del servidor no tiene el formato esperado. No se ha aplicado ningún cambio.',
  },
};

/**
 * The shape of the operator half of a catalogue: this file's keys, with every value widened to
 * `string`.
 *
 * Deriving it from the Spanish catalogue rather than declaring it separately is what makes the two
 * languages impossible to drift at the type level. The runtime gate still checks the same property,
 * because a catalogue can also be assembled at run time from a fixture, and a gate that can only be
 * satisfied by the compiler cannot be shown to refuse anything.
 */
export type OperatorCatalogue = typeof es;

/**
 * Every key this application has, in one type: this file's, plus the administration half's.
 *
 * **The two halves are separate MODULES and one key space.** `admin-es.ts` is loaded with the
 * administration chunk and registered into the same `ui` namespace, so `t('admin.users.title')` is
 * written the same way wherever it appears; this union is what makes the compiler agree. A screen
 * outside the section that named an `admin.*` key would type-check and then render the key at run
 * time, which is why `features/admin/catalogue.ts` states where the registration happens and
 * `typed-confirm.tsx` — the one shared component that used to reach into `admin.confirm.*` — was
 * moved onto top-level `confirm.*` keys instead.
 */
export type UiCatalogue = OperatorCatalogue & AdminCatalogue;
